import type {
  CreateExpenseCategoryPayload,
  CreateExpensePayload,
  CreateRecurringTemplatePayload,
  Expense,
  ExpenseCategory,
  RecurringTemplate,
  RecurringTemplateFilters,
  UpdateExpenseCategoryPayload,
  UpdateExpensePayload,
  UpdateRecurringTemplatePayload,
} from "@/features/expenses/types";
import { getBackendClient, type QueryBuilder } from "@/lib/backend";
import { orderListPage } from "@/lib/db/list-order";
import { fetchListTotals } from "@/lib/db/list-totals";
import { assertProfileId } from "@/lib/profile-id";
import { normalizeDateOnlyInput, toPostgrestIlikeContains } from "@/lib/utils/query-helpers";

const backend = getBackendClient();

const EXPENSE_SELECT = `
  id, amount, category, description, date, expense_date, receipt_url, created_at, updated_at,
  gl_expense_categories!gl_expenses_category_id_fkey (
    id, category, description, color_code, icon_name, budget_amount, budget_period,
    budget_alert_threshold, is_active, sort_order
  )
`;

const EXPENSE_CATEGORY_SELECT = `
  id, category, description, color_code, icon_name, budget_amount, budget_period,
  budget_alert_threshold, is_active, sort_order
`;

const RECURRING_TEMPLATE_SELECT = `
  id, template_name, description, amount, frequency_type, frequency_interval,
  start_date, end_date, next_occurrence, is_active, notes, category_id,
  created_at, updated_at,
  gl_expense_categories!gl_recurring_expense_templates_category_id_fkey (
    id, category, color_code, icon_name
  )
`;

function normalizeOptionalDateOnlyInput(value?: string): string | null {
  if (value === undefined) {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  return trimmed.split("T")[0] ?? null;
}

// --- Expenses ---

/**
 * The filters the expense list narrows by, applied in one place so the list,
 * the export, the totals RPC and the category breakdown cannot select four
 * different sets.
 *
 * Search matches the `search_text` computed column, which reaches description,
 * category name and description, amount text and submitter in one request. The
 * same function backs `expense_list_totals`, so the footer and the rows are
 * filtered by identical SQL rather than by two hand-kept-in-step predicates.
 */
export interface ExpenseListFilters {
  category_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
  search?: string;
  sort_direction?: "asc" | "desc";
  sort_field?: string;
}

/**
 * The midnight after the given date, as the exclusive upper bound of that day.
 *
 * `null` for anything unparseable. These filters arrive from the query string,
 * so `?date_to=invalid` reaches here verbatim, and `toISOString()` on the
 * resulting Invalid Date throws a `RangeError` — which replaced the list with a
 * raw error message. An unreadable bound is dropped instead.
 */
function nextDayBoundary(value: string): string | null {
  const dateOnly = value.split("T")[0] ?? value;
  const parsed = new Date(`${dateOnly}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  parsed.setUTCDate(parsed.getUTCDate() + 1);
  return parsed.toISOString().split("T")[0] ?? dateOnly;
}

/**
 * Dates filter on `expense_date`, never `date`.
 *
 * `date` is the Glide import stamp: equal to `created_at` on all 1,057 imported
 * rows and holding 3 distinct values, so filtering calendar 2024 by it returned
 * 0 rows where `expense_date` returned 500. `createExpense`/`updateExpense`
 * write both columns to the same value, so rows created in-app agree and only
 * the imported ones diverge — which makes `expense_date` correct for both.
 */
function applyExpenseListFilters<T>(
  query: QueryBuilder<T>,
  params: ExpenseListFilters,
): QueryBuilder<T> {
  let next = query;

  if (params.category_id) {
    next = next.eq("category_id", params.category_id);
  }
  if (params.date_from) {
    next = next.gte("expense_date", params.date_from.split("T")[0]);
  }
  if (params.date_to) {
    // Exclusive against the next midnight: `expense_date` is a timestamptz and
    // the control is a date picker, so `lte` against a bare date would drop
    // every expense recorded later on the closing day.
    const boundary = nextDayBoundary(params.date_to);
    if (boundary) {
      next = next.lt("expense_date", boundary);
    }
  }
  if (params.search?.trim()) {
    const search = toPostgrestIlikeContains(params.search.trim());
    next = next.or(`search_text.ilike.${search}`);
  }

  return next;
}

/**
 * Columns the expense list may be ordered by, as they appear in the URL.
 *
 * Canonical here rather than in the search params because this is the seam that
 * reaches the database: the value ends up in PostgREST's `order` clause, so it
 * is re-checked at the point of use instead of trusting every caller to have
 * sanitized it first.
 *
 * `expense_date` rather than `date`: the `date` column is the Glide import
 * stamp, equal to `created_at` on all 1,057 imported rows and holding only 3
 * distinct values, so ordering by it produces one enormous tie group. See the
 * migration adding `expense_list_totals` for the measurements.
 */
export const EXPENSE_SORT_FIELDS = ["amount", "category", "description", "expense_date"] as const;

export type ExpenseSortField = (typeof EXPENSE_SORT_FIELDS)[number];

const DEFAULT_EXPENSE_SORT: ExpenseSortField = "expense_date";

/**
 * Sort the category column by the name the row actually displays.
 *
 * `gl_expenses.category` is the name as it stood when the row was written, so
 * ordering by it puts renamed categories where they used to belong rather than
 * where the reader sees them. Ordering through the embedding sorts by the
 * current record, which is what the cell renders.
 */
const EXPENSE_ORDER_EXPRESSIONS: Record<ExpenseSortField, string> = {
  amount: "amount",
  category: "gl_expense_categories(category)",
  description: "description",
  expense_date: "expense_date",
};

/** Anything outside the whitelist falls back to the default order. */
function expenseOrderExpression(field?: string): string {
  const allowed = EXPENSE_SORT_FIELDS.find((candidate) => candidate === field);
  return EXPENSE_ORDER_EXPRESSIONS[allowed ?? DEFAULT_EXPENSE_SORT];
}

/**
 * Lists expenses with optional search, category and date filters.
 *
 * Ordering is a server concern: the list renders one page at a time, so a sort
 * applied after the fetch would only reshuffle that page. `orderListPage` closes
 * the order with the unique tiebreaker a paged read needs — without it, ordering
 * by a column with few distinct values duplicates and drops rows across page
 * boundaries.
 */
export async function getExpenses(params?: ExpenseListFilters) {
  const limit = params?.limit ?? 50;
  const offset = Math.max(0, params?.offset ?? 0);

  let query = orderListPage(backend.data.from("gl_expenses").select(EXPENSE_SELECT), {
    ascending: params?.sort_direction === "asc",
    column: expenseOrderExpression(params?.sort_field),
  }).range(offset, offset + limit - 1);

  query = applyExpenseListFilters(query, params ?? {});

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }

  return (data || []) as Expense[];
}

/** The filter arguments both expense aggregates take, built once. */
function nullableDate(value?: string): string | null {
  return value?.split("T")[0] || null;
}

function nullableSearch(value?: string): string | null {
  return value?.trim() || null;
}

function expenseTotalsArgs(params?: ExpenseListFilters): Record<string, unknown> {
  return {
    p_category_id: params?.category_id || null,
    p_date_from: nullableDate(params?.date_from),
    p_date_to: nullableDate(params?.date_to),
    p_search: nullableSearch(params?.search),
  };
}

/** The aggregate fields `expense_list_totals` returns. */
const EXPENSE_TOTAL_FIELDS = ["row_count", "total_amount"] as const;

export type ExpenseListTotals = Record<(typeof EXPENSE_TOTAL_FIELDS)[number], number>;

/**
 * Count and total spend for the whole filtered set, from the server.
 *
 * Takes the same filters `getExpenses` applies, so the strip under the table and
 * the rows in it describe one set. This replaces a reduce over the visible page,
 * which reported one 25-row page as total spending.
 */
export function getExpenseTotals(params?: ExpenseListFilters): Promise<ExpenseListTotals> {
  return fetchListTotals("expense_list_totals", expenseTotalsArgs(params), EXPENSE_TOTAL_FIELDS);
}

export interface ExpenseCategoryTotal {
  category_id: string | null;
  category_name: string;
  row_count: number;
  total_amount: number;
}

/**
 * Spending split by category over the whole filtered set.
 *
 * Grouped server-side for the same reason as the totals: the old breakdown
 * grouped the visible page, so every category was understated and the ranking
 * reshuffled as the operator paged.
 */
export async function getExpenseCategoryTotals(
  params?: ExpenseListFilters & { limit_categories?: number },
): Promise<ExpenseCategoryTotal[]> {
  const { data, error } = await backend.data.rpc("expense_category_totals", {
    ...expenseTotalsArgs(params),
    p_limit: params?.limit_categories ?? 5,
  });

  if (error) {
    throw new Error(error.message);
  }

  return (Array.isArray(data) ? data : []).map((row) => {
    const record = row as Record<string, unknown>;
    return {
      category_id: (record.category_id as string | null) ?? null,
      category_name:
        typeof record.category_name === "string" ? record.category_name : "Uncategorized",
      row_count: Number(record.row_count ?? 0),
      total_amount: Number(record.total_amount ?? 0),
    };
  });
}

/** Fetches one expense by id with its category metadata. */
export async function getExpenseById(id: string) {
  const { data, error } = await backend.data
    .from("gl_expenses")
    .select(EXPENSE_SELECT)
    .eq("id", id)
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data as Expense;
}

/** SQLSTATE `xan_update_expense` raises when the row changed after it was read. */
const EXPENSE_TARGET_CHANGED = "PT409";
const EXPENSE_CHANGED_MESSAGE =
  "This expense changed after you opened it. Reopen it and try again.";

/**
 * Creates an expense through the idempotent seam: retrying the same
 * `commandId` with the same payload returns the first result, never a second row.
 */
export async function createExpense(commandId: string, payload: CreateExpensePayload) {
  const { data, error } = await backend.data.rpc<Expense>("xan_create_expense", {
    p_command_id: commandId,
    p_payload: {
      amount: payload.amount,
      date: normalizeDateOnlyInput(payload.date ?? payload.expense_date),
      description: payload.description || null,
      category_id: payload.category_id?.trim() || null,
      receipt_url: payload.receipt_url || null,
    },
  });

  if (error) {
    throw new Error(error.message);
  }

  return data as Expense;
}

/**
 * Updates the expense the form opened, refusing the save when the row changed
 * after it was read instead of overwriting that change. Unlike accounts and
 * projects, the opened row's version is used as is: nothing recalculates an
 * expense, and only unlinking a deleted category or template stamps
 * `updated_at` without an edit.
 */
export async function updateExpense(
  opened: Pick<Expense, "id" | "updated_at">,
  payload: UpdateExpensePayload,
) {
  const patch: {
    amount?: number;
    category_id?: string | null;
    description?: string | null;
    date?: string;
    receipt_url?: string | null;
  } = {};

  if (payload.amount !== undefined) {
    patch.amount = payload.amount;
  }
  if (payload.category_id !== undefined) {
    patch.category_id = payload.category_id?.trim() || null;
  }
  if (payload.description !== undefined) {
    patch.description = payload.description;
  }

  const normalizedDate =
    normalizeOptionalDateOnlyInput(payload.date) ??
    normalizeOptionalDateOnlyInput(payload.expense_date);
  if (normalizedDate) {
    patch.date = normalizedDate;
  }

  if (payload.receipt_url !== undefined) {
    patch.receipt_url = payload.receipt_url;
  }

  if (Object.keys(patch).length === 0) {
    throw new Error("No fields to update");
  }

  const { data, error } = await backend.data.rpc<Expense>("xan_update_expense", {
    p_expense_id: opened.id,
    p_expected_updated_at: opened.updated_at,
    p_patch: patch,
  });

  if (error?.code === EXPENSE_TARGET_CHANGED) {
    throw new Error(EXPENSE_CHANGED_MESSAGE);
  }
  if (error) {
    throw new Error(error.message);
  }

  return data as Expense;
}

/** Soft-deletes an expense through the backend RPC. */
export async function deleteExpense(id: string) {
  const { error } = await backend.data.rpc("xan_soft_delete_expense", {
    p_expense_id: id,
  });

  if (error) {
    throw new Error(error.message);
  }

  return { success: true };
}

// --- Expense Categories ---

/** Lists expense categories, excluding inactive categories by default. */
export async function getExpenseCategories(params?: { include_inactive?: boolean }) {
  let query = backend.data
    .from("gl_expense_categories")
    .select(EXPENSE_CATEGORY_SELECT)
    .order("sort_order", { ascending: true })
    .order("category", { ascending: true });

  if (params?.include_inactive !== true) {
    query = query.eq("is_active", true);
  }

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }

  return (data || []) as ExpenseCategory[];
}

/** Creates an expense category used for budgets and recurring templates. */
export async function createExpenseCategory(payload: CreateExpenseCategoryPayload) {
  const insertRow: Record<string, unknown> = {
    category: payload.category,
    description: payload.description ?? null,
    color_code: payload.color_code ?? null,
    icon_name: payload.icon_name ?? null,
    budget_amount: payload.budget_amount ?? null,
    sort_order: payload.sort_order ?? 0,
    is_active: payload.is_active,
  };
  // Omitted rather than nulled: the column defaults ('monthly', 80) are the
  // policy a category without an explicit choice should get, and NULL would
  // override them.
  if (payload.budget_period != null) {
    insertRow.budget_period = payload.budget_period;
  }
  if (payload.budget_alert_threshold != null) {
    insertRow.budget_alert_threshold = payload.budget_alert_threshold;
  }

  const { data, error } = await backend.data
    .from("gl_expense_categories")
    .insert(insertRow)
    .select(EXPENSE_CATEGORY_SELECT)
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data as ExpenseCategory;
}

/** The category columns an update may touch; anything else in the payload is ignored. */
const UPDATABLE_CATEGORY_FIELDS = [
  "budget_alert_threshold",
  "budget_amount",
  "budget_period",
  "category",
  "color_code",
  "description",
  "icon_name",
  "is_active",
  "sort_order",
] as const satisfies readonly (keyof UpdateExpenseCategoryPayload)[];

/** Updates editable fields on an expense category. */
export async function updateExpenseCategory(id: string, payload: UpdateExpenseCategoryPayload) {
  const updates: Record<string, unknown> = Object.fromEntries(
    UPDATABLE_CATEGORY_FIELDS.filter((field) => payload[field] !== undefined).map((field) => [
      field,
      payload[field],
    ]),
  );

  if (Object.keys(updates).length === 0) {
    throw new Error("No fields to update");
  }

  const { data, error } = await backend.data
    .from("gl_expense_categories")
    .update(updates)
    .eq("id", id)
    .select(EXPENSE_CATEGORY_SELECT)
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data as ExpenseCategory;
}

/** Deletes an unused expense category after checking references. */
export async function deleteExpenseCategory(id: string) {
  const { count, error: countError } = await backend.data
    .from("gl_expenses")
    .select("id", { count: "exact", head: true })
    .eq("category_id", id);

  if (countError) {
    throw new Error(countError.message);
  }
  if (count && count > 0) {
    throw new Error(
      `Cannot delete category: ${count} expense(s) still reference it. Deactivate instead.`,
    );
  }

  const { error } = await backend.data.from("gl_expense_categories").delete().eq("id", id);

  if (error) {
    throw new Error(error.message);
  }

  return { success: true };
}

// --- Recurring Templates ---

/** Lists recurring expense templates with optional filters. */
export async function getRecurringTemplates(filters?: RecurringTemplateFilters) {
  let query = backend.data
    .from("gl_recurring_expense_templates")
    .select(RECURRING_TEMPLATE_SELECT)
    .order("next_occurrence", { ascending: true });

  if (filters?.is_active !== undefined) {
    query = query.eq("is_active", filters.is_active);
  }
  if (filters?.category_id) {
    query = query.eq("category_id", filters.category_id);
  }
  if (filters?.frequency_type) {
    query = query.eq("frequency_type", filters.frequency_type);
  }
  if (filters?.search) {
    query = query.ilike("template_name", `%${filters.search}%`);
  }

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }
  return (data || []) as RecurringTemplate[];
}

/** Creates a recurring expense template attributed to the authenticated profile. */
export async function createRecurringTemplate(
  payload: CreateRecurringTemplatePayload,
  profileId: string,
) {
  const authenticatedProfileId = assertProfileId(profileId);

  const { data, error } = await backend.data
    .from("gl_recurring_expense_templates")
    .insert({
      template_name: payload.template_name,
      amount: payload.amount,
      category_id: payload.category_id,
      description: payload.description ?? null,
      notes: payload.notes ?? null,
      frequency_type: payload.frequency_type,
      frequency_interval: payload.frequency_interval,
      start_date: payload.start_date,
      end_date: payload.end_date ?? null,
      next_occurrence: payload.start_date,
      is_active: payload.is_active ?? true,
      created_by: authenticatedProfileId,
    })
    .select(RECURRING_TEMPLATE_SELECT)
    .single();

  if (error) {
    throw new Error(error.message);
  }
  return data as RecurringTemplate;
}

/** Updates editable fields on a recurring expense template. */
export async function updateRecurringTemplate(id: string, payload: UpdateRecurringTemplatePayload) {
  const updates: Record<string, unknown> = {};
  if (payload.template_name !== undefined) {
    updates.template_name = payload.template_name;
  }
  if (payload.amount !== undefined) {
    updates.amount = payload.amount;
  }
  if (payload.category_id !== undefined) {
    updates.category_id = payload.category_id;
  }
  if (payload.description !== undefined) {
    updates.description = payload.description;
  }
  if (payload.notes !== undefined) {
    updates.notes = payload.notes;
  }
  if (payload.frequency_type !== undefined) {
    updates.frequency_type = payload.frequency_type;
  }
  if (payload.frequency_interval !== undefined) {
    updates.frequency_interval = payload.frequency_interval;
  }
  if (payload.start_date !== undefined) {
    updates.start_date = payload.start_date;
  }
  if (payload.end_date !== undefined) {
    updates.end_date = payload.end_date;
  }
  if (payload.is_active !== undefined) {
    updates.is_active = payload.is_active;
  }

  if (Object.keys(updates).length === 0) {
    throw new Error("No fields to update");
  }

  const { data, error } = await backend.data
    .from("gl_recurring_expense_templates")
    .update(updates)
    .eq("id", id)
    .select(RECURRING_TEMPLATE_SELECT)
    .single();

  if (error) {
    throw new Error(error.message);
  }
  return data as RecurringTemplate;
}

/** Deletes a recurring expense template. */
export async function deleteRecurringTemplate(id: string) {
  const { error } = await backend.data.from("gl_recurring_expense_templates").delete().eq("id", id);

  if (error) {
    throw new Error(error.message);
  }
  return { success: true };
}

/** SQLSTATEs `xan_process_recurring_template` raises for a template that moved or is gone since it was opened. */
const RECURRING_TARGET_CHANGED = "PT409";
const RECURRING_NOT_FOUND = "PT404";
const RECURRING_CHANGED_MESSAGE =
  "This template changed after you opened it. Refresh the list and try again.";
const RECURRING_GONE_MESSAGE = "This template no longer exists. Refresh the list.";

/** What processing a recurring template occurrence did. */
interface ProcessedRecurringTemplate {
  /** False when the occurrence was already processed and its earlier expense is returned. */
  created: boolean;
  expense_id: string;
  /** The template's `next_occurrence` after processing. */
  next_occurrence: string;
}

interface ProcessRecurringTemplateResponse {
  created: boolean;
  expense: { id: string };
  next_occurrence: string;
}

/**
 * Creates the expense for the template occurrence the operator opened and
 * advances the template, in one backend transaction. Processing the same
 * occurrence again, from a retry or a colleague's tab, returns its first
 * expense instead of creating a second; a template that moved without an
 * expense for the opened occurrence is refused.
 */
export async function processRecurringTemplate(
  template: Pick<RecurringTemplate, "id" | "next_occurrence">,
): Promise<ProcessedRecurringTemplate> {
  const occurrence = normalizeOptionalDateOnlyInput(template.next_occurrence);
  if (!occurrence) {
    throw new Error("Template has no next occurrence to process.");
  }

  const { data, error } = await backend.data.rpc<ProcessRecurringTemplateResponse>(
    "xan_process_recurring_template",
    { p_template_id: template.id, p_expected_occurrence: occurrence },
  );

  if (error?.code === RECURRING_TARGET_CHANGED) {
    throw new Error(RECURRING_CHANGED_MESSAGE);
  }
  if (error?.code === RECURRING_NOT_FOUND) {
    throw new Error(RECURRING_GONE_MESSAGE);
  }
  if (error) {
    throw new Error(error.message);
  }
  if (!data?.expense?.id) {
    throw new Error("Processing the template returned no expense.");
  }

  return {
    created: data.created,
    expense_id: data.expense.id,
    next_occurrence: data.next_occurrence,
  };
}
